// src/components/tour-types/forms/CreateTourType.tsx
import React, { useEffect, useState } from "react";
import Field from "@/components/shared/ui/Field";
import Input from "@/components/shared/ui/Input";
import { FiType, FiFileText } from "react-icons/fi";

export type UpsertTourType = {
  name: string;
  description?: string;
};

export default function CreateTourType({
  onSubmit,
  onCancel,
  defaultValues,
}: {
  onSubmit: (payload: UpsertTourType) => void;
  onCancel?: () => void;
  defaultValues?: Partial<UpsertTourType>;
}) {
  const [form, setForm] = useState<UpsertTourType>({
    name: defaultValues?.name ?? "",
    description: defaultValues?.description ?? "",
  });

  // If the modal is reused with different defaults, sync once it opens
  useEffect(() => {
    setForm((p) => ({
      ...p,
      name: defaultValues?.name ?? "",
      description: defaultValues?.description ?? "",
    }));
  }, [defaultValues?.name, defaultValues?.description]);

  const change = <K extends keyof UpsertTourType>(
    k: K,
    v: UpsertTourType[K]
  ) => setForm((p) => ({ ...p, [k]: v }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSubmit({
      name: form.name.trim(),
      description: form.description?.trim() || "",
    });
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* Name */}
      <Field label="Tour type name" icon={<FiType />}>
        <Input
          value={form.name}
          onChange={(e) => change("name", e.target.value)}
          placeholder="e.g., Family, Honeymoon, Group"
          required
        />
      </Field>

      {/* Description */}
      <Field label="Description" icon={<FiFileText />}>
        <Input
          value={form.description ?? ""}
          onChange={(e) => change("description", e.target.value)}
          placeholder="Short internal description (optional)"
        />
      </Field>

      {/* Actions */}
      <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
        <button
          type="button"
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          onClick={() => onCancel?.()}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Create Tour Type
        </button>
      </div>
    </form>
  );
}
